import { INDUSTRIES } from '../controls.js';
import { KINTA_QUESTIONS } from '../fixtures/kinta.js';
import { CONTROL_LIBRARY, controlAnswers } from './controls.js';
import { DOCUMENT_TYPES } from './document-types.js';
import { libraryProbes, PROBE_PARENT, probeKeys } from './probes.js';
import { compileForms, FORMS_QUESTION, formQuestionNeeds, InsurerFormDefinition } from '../insurer-forms.js';
import { INSURER_FORMS } from './insurer-forms/index.js';
import { industryProfiles, PROFILE_KILL_SWITCHES } from './profiles.js';
import { STARTER_RULES } from './rules.js';
import { UNDERWRITING_FACTS, UW_STAGES } from './underwriting-facts.js';
/**
 * SME Cyber Assessment · Manufacturing (MY): the platform's seed journey, built from the control library.
 *
 * - Every control that applies to a manufacturer becomes a question (keyed by its control id), except where
 *   a hand-written prototype question already asks it.
 * - Profile questions in S01 decide which conditional controls are asked (own servers, remote access, ...).
 * - Mandatory: kill switches and controls of criticality 8 or more. The rest are asked after them.
 * - Answers record how far the control is in place (score_change), raise a finding when it is not, ask for
 *   evidence when it is, and flag kill switches.
 */
export const MFG_MY_JOURNEY = 'JRN-SME-MFG-MY';
const yesNo = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];
/** The industries the Cyber Security Act 2024 names among its NCII sectors (task 84). */
export const NCII_SECTORS = ['energy', 'utilities', 'telecoms', 'government', 'financial_services', 'healthcare', 'logistics']; // prettier-ignore
const PROFILE_QUESTIONS = {
    'P-SERVERS': {
        type: 'boolean',
        text: 'Do you run any servers of your own, in the office or in a data centre?',
        help: 'Not counting cloud services such as Microsoft 365 or Google Workspace.',
        mandatory: true,
        options: yesNo,
    },
    'P-REMOTE': {
        type: 'boolean',
        text: 'Can staff or suppliers connect to your office systems from outside, for example by VPN or remote desktop?',
        mandatory: true,
        options: yesNo,
    },
    'P-WEBSITE': {
        type: 'boolean',
        text: 'Does the business run a website, online store or customer portal?',
        mandatory: true,
        options: yesNo,
    },
    'P-IAAS': {
        type: 'boolean',
        text: 'Do you run systems on cloud platforms such as AWS, Azure or Google Cloud?',
        help: 'Virtual servers or storage you manage yourself, not ready-made apps.',
        mandatory: true,
        options: yesNo,
    },
    // Task 82: a licensed institution is asked the financial pack at BNM RMiT depth; a smaller firm isn't.
    'P-FS-TIER': {
        type: 'single_choice',
        text: 'Is the business licensed or approved by Bank Negara Malaysia?',
        help: 'Banks, insurers and takaful operators, e-money issuers and payment system operators are. Moneylenders, brokers and agents usually are not.',
        mandatory: true,
        visibility: { fact: 'industry', op: 'eq', value: 'financial_services' },
        options: [
            { value: 'licensed', label: 'Yes, licensed or approved by BNM' },
            { value: 'other', label: 'No, a smaller firm such as a moneylender, broker or agent' },
        ],
    },
    // Task 83: telehealth brings its own platform and records to protect.
    'P-TELEHEALTH': {
        type: 'boolean',
        text: 'Do you consult patients remotely, by video or phone?',
        help: 'Telehealth or virtual consultations, including follow-ups and prescriptions given remotely.',
        mandatory: true,
        visibility: { fact: 'industry', op: 'eq', value: 'healthcare' },
        options: yesNo,
    },
    // Task 84: remote and offshore sites, and the duties the Cyber Security Act 2024 puts on NCII entities.
    'P-SITES': {
        type: 'boolean',
        text: 'Do you run unmanned or offshore sites, such as platforms, pipelines, substations or pumping stations?',
        mandatory: true,
        visibility: { fact: 'industry', op: 'in', value: ['energy', 'utilities'] },
        options: yesNo,
    },
    'P-NCII': {
        type: 'boolean',
        text: 'Has the government designated the business a national critical information infrastructure (NCII) entity?',
        help: 'Under the Cyber Security Act 2024, through your sector lead. Answer no if you only supply one.',
        mandatory: true,
        visibility: { fact: 'industry', op: 'in', value: [...NCII_SECTORS] },
        options: yesNo,
    },
};
/**
 * Who the business relies on (task 75, decision 0054): optional and unscored, for the insurer's view of
 * suppliers shared across its book. Email is Q021's answer. Names are free text; the platform matches them.
 */
const supplier = (text, help) => ({
    type: 'entity_selector',
    text,
    help,
    settings: { entity_type: 'supplier', multiple: true },
    metadata: { risk_domain: 'TP' },
});
const SUPPLIER_QUESTION_DEFS = {
    'SUP-IT': supplier('Which outside companies look after your IT, if any?', 'Their names, for example the firm that set up your network or answers your support calls. Skip this if you do it all yourselves.'), // prettier-ignore
    'SUP-CLOUD': supplier('Which cloud services hold your business data?', 'For example Microsoft 365, Google Workspace, Dropbox or AWS.'), // prettier-ignore
    'SUP-ACCT': supplier('Which accounting or payroll software or provider do you use?', 'For example SQL Account, AutoCount, Xero, or an outside bookkeeper.'), // prettier-ignore
    'SUP-PAY': supplier('Who handles your card or online payments?', 'For example iPay88, Stripe, Billplz, or your bank’s card terminal.'), // prettier-ignore
};
/** Which answer makes a profile-dependent control apply. */
const PROFILE_CONDITION = {
    staff_50_plus: { question: 'Q012', op: 'gte', value: 50 },
    cloud_email: { question: 'Q021', op: 'in', value: ['microsoft_365', 'google_workspace'] },
    own_servers: { question: 'P-SERVERS', op: 'eq', value: 'yes' },
    remote_access: { question: 'P-REMOTE', op: 'eq', value: 'yes' },
    website: { question: 'P-WEBSITE', op: 'eq', value: 'yes' },
    cloud_iaas: { question: 'P-IAAS', op: 'eq', value: 'yes' },
    bnm_licensed: { question: 'P-FS-TIER', op: 'eq', value: 'licensed' },
    telehealth: { question: 'P-TELEHEALTH', op: 'eq', value: 'yes' },
    remote_sites: { question: 'P-SITES', op: 'eq', value: 'yes' },
    ncii: { question: 'P-NCII', op: 'eq', value: 'yes' },
};
/** Controls a hand-written question already asks, for every SME. */
const COVERED = { 'IA-02': 'Q058', 'BR-03': 'Q283' };
/** How far each answer to those questions puts the control in place, as for the generated questions. */
const COVERED_SCORES = {
    Q058: { everyone: 1, some: 0.5, no: 0 },
    Q283: { last_90_days: 1, '3_12_months': 0.5, never: 0 },
};
const STAGE_OF_DOMAIN = {
    GV: 'S01', AM: 'S02', NS: 'S02', DP: 'S03', TP: 'S03', IA: 'S04',
    EP: 'S05', VM: 'S05', EH: 'S05', BR: 'S05', IR: 'S07', LD: 'S07',
}; // prettier-ignore
const STAGE_OF_CONTROL = { 'GV-11': 'S08' };
/** Hand-written and profile questions, first in their stage, in this order. */
const FIRST = {
    S01: [
        'Q012',
        'Q021',
        'Q031',
        'P-SERVERS',
        'P-REMOTE',
        'P-WEBSITE',
        'P-IAAS',
        'P-FS-TIER',
        'P-TELEHEALTH',
        'P-SITES',
        'P-NCII',
        'Q301',
        'Q104',
        'Q105',
        'Q106',
    ],
    S02: [],
    S03: ['Q330', 'SUP-IT', 'SUP-CLOUD', 'SUP-ACCT', 'SUP-PAY'],
    S04: ['Q058'],
    S05: ['Q283'],
    S06: ['Q044'],
    S07: [],
    S08: ['Q402', FORMS_QUESTION],
    S09: [],
    S10: [...UW_STAGES.S10],
    S11: [...UW_STAGES.S11],
    S12: [...UW_STAGES.S12],
};
/** Industries with a pack of their own besides manufacturing. */
const OTHER_INDUSTRIES = Object.keys(INDUSTRIES).filter((i) => i !== 'manufacturing');
const STAGES = {
    S01: {
        name: 'Know your business',
        description: 'Who you are, how big, what you use and how you are governed.',
        completion_message: 'Nice. We now know enough to tailor the rest of the check.',
        routes: [
            {
                after: 'Q104',
                when: { question: 'Q104', op: 'eq', value: 'no' },
                then: { action: 'skip_questions', questions: ['Q105', 'Q106'] },
                label: 'Not certified to ISO 27001',
            },
        ],
    },
    S02: {
        name: 'Digital footprint',
        mode: 'parallel',
        description: 'Your devices, software and network, and anything reachable from the internet.',
        completion_message: 'We know what you run and what an attacker can see from outside.',
    },
    S03: {
        name: 'Data',
        mode: 'parallel',
        description: 'What personal and sensitive data you hold, where it lives, and which suppliers touch it.',
        completion_message: 'We will use this to size the cost of a data breach.',
    },
    S04: {
        name: 'Identity',
        description: 'Sign-ins, MFA, admin accounts and leavers.',
        completion_message: 'Identity is where most SME breaches start. You are ahead of most.',
    },
    S05: {
        name: 'Ransomware',
        description: 'Endpoint protection, patching, email and backups.',
        completion_message: 'Ransomware is the most expensive SME claim. This stage moves your price the most.',
        // Without the basics, the controls built on them can't be in place: counted so, not asked (task 85).
        routes: [
            {
                after: 'BR-01',
                when: { question: 'BR-01', op: 'eq', value: 'no' },
                then: { action: 'skip_questions', questions: ['BR-04', 'BR-07', 'BR-08', 'BR-09', 'BR-13'] },
                skipped_as: 'not_in_place',
                label: 'No automatic backups',
            },
            {
                after: 'EP-01',
                when: { question: 'EP-01', op: 'eq', value: 'no' },
                then: { action: 'skip_questions', questions: ['EP-10'] },
                skipped_as: 'not_in_place',
                label: 'No endpoint detection and response',
            },
            {
                after: 'VM-09',
                when: { question: 'VM-09', op: 'eq', value: 'not_in_place' },
                then: { action: 'skip_questions', questions: ['VM-10'] },
                label: 'No penetration test yet',
            },
        ],
    },
    S06: {
        name: 'OT security',
        mode: 'conditional',
        description: 'Machines, controllers, vendor remote access and IT/OT separation.',
        entry: { question: 'Q031', op: 'eq', value: 'yes' },
        mandatory: false,
        completion_message: 'Production downtime is your biggest loss driver. This stage protects it.',
        routes: [
            {
                after: 'Q044',
                when: { question: 'Q044', op: 'eq', value: 'on_site' },
                then: { action: 'skip_questions', questions: ['TP-10'] },
                label: 'Vendors only work on site',
            },
        ],
    },
    // The industry packs other than manufacturing's (decision 0034): each question shows only to its industry.
    S09: {
        name: 'Your industry',
        mode: 'parallel',
        description: 'Checks for your kind of business: patient records, card payments, client files, fleets or financial data.',
        entry: { fact: 'industry', op: 'in', value: OTHER_INDUSTRIES },
        completion_message: 'That covers the risks particular to your industry.',
    },
    S07: {
        name: 'Recovery',
        description: 'Incident plan, who to call, how fast you can restart, and how you would spot an attack.',
        completion_message: 'You now have a recovery plan an insurer can rely on.',
        routes: [
            {
                after: 'LD-04',
                when: { question: 'LD-04', op: 'eq', value: 'no' },
                then: { action: 'skip_questions', questions: ['LD-09', 'LD-06'] },
                skipped_as: 'not_in_place',
                label: 'Audit logging is off',
            },
        ],
    },
    S08: {
        name: 'Insurance readiness',
        description: 'Policy, limits, and the conditions an insurer will set.',
        completion_message: 'Your readiness report is ready to share with an insurer.',
    },
    // Task 89: what the chosen insurers' proposal forms ask beyond the controls. Entered once the
    // assessment prepares a form; each question shows only when a chosen form needs it.
    S10: {
        name: 'Your company, for the insurer',
        mode: 'conditional',
        description: 'Registration, revenue, the group, past incidents and the cover you want.',
        entry: { question: FORMS_QUESTION, op: 'answered' },
        // Optional for the Cyber Check: readiness for each insurer is measured on its form (task 91).
        mandatory: false,
        completion_message: 'The insurer now knows who it is covering.',
    },
    S11: {
        name: 'Data and access, for the insurer',
        mode: 'conditional',
        description: 'The records you hold, how they are protected, and who can reach your systems.',
        entry: { question: FORMS_QUESTION, op: 'answered' },
        mandatory: false,
        completion_message: 'That covers what a data breach would expose.',
    },
    S12: {
        name: 'Resilience and monitoring, for the insurer',
        mode: 'conditional',
        description: 'Backups, critical systems, monitoring, patching and suppliers, in the insurer’s terms.',
        entry: { question: FORMS_QUESTION, op: 'answered' },
        mandatory: false,
        completion_message: 'Your proposal forms are ready for your broker to check.',
    },
};
function controlEffects(id, c) {
    const ks = c.kill_switch ?? false;
    return (implementation) => {
        if (implementation === null)
            return [
                { type: 'create_task', params: { control: id, task: 'confirm' } },
                ...(ks ? [{ type: 'require_human_review', params: { control: id } }] : []),
            ];
        return [
            { type: 'score_change', params: { control: id, implementation } },
            ...(implementation === 0
                ? [{ type: 'create_finding', params: { control: id, ...(ks ? { kill_switch: true } : {}) } }]
                : []),
            ...(implementation === 1 && c.best_evidence !== 'self'
                ? [{ type: 'request_evidence', params: { control: id, evidence: c.best_evidence } }]
                : []),
        ];
    };
}
/** A hand-written question gets its control's effects on each option, keeping any it already has. */
function withControlEffects(q, id, scores) {
    const effects = controlEffects(id, CONTROL_LIBRARY[id]);
    return {
        ...q,
        options: q.options.map((o) => ({
            ...o,
            effects: [...(o.effects ?? []), ...effects(scores[o.value] ?? null)],
        })),
    };
}
function controlQuestion(id, c) {
    const ks = c.kill_switch ?? false;
    const effects = controlEffects(id, c);
    const fw = c.frameworks;
    const tests = [
        ...(c.applies?.industries
            ? [{ fact: 'industry', op: 'in', value: c.applies.industries }]
            : []),
        ...(c.applies?.profile ? [PROFILE_CONDITION[c.applies.profile]] : []),
    ];
    const visibility = tests.length > 1 ? { all: tests } : tests[0];
    return {
        type: 'single_choice',
        text: c.question,
        mandatory: ks || c.criticality >= 8,
        ...(visibility ? { visibility } : {}),
        options: controlAnswers(id).map((a) => ({
            value: a.value,
            label: a.label,
            effects: effects(a.implementation),
        })),
        metadata: {
            risk_domain: c.domain,
            controls: [id],
            frameworks: [
                `NIST CSF ${fw.nist_csf}`,
                ...(fw.cis ? [`CIS ${fw.cis}`] : []),
                ...(fw.iso_27001 ? [`ISO 27001 ${fw.iso_27001}`] : []),
                ...(c.regulation ? [c.regulation] : []),
            ],
            evidence: [c.best_evidence],
            kill_switch: ks,
        },
    };
}
/**
 * Everything the platform publishes for this journey: the whole control library, questions, stages, the
 * journey and the starter rules.
 */
/** The hand-written and profile questions, with the hand-written ones' control effects. */
function baseQuestions() {
    const questions = { ...KINTA_QUESTIONS, ...PROFILE_QUESTIONS, ...SUPPLIER_QUESTION_DEFS, ...libraryProbes(), ...insurerQuestions() }; // prettier-ignore
    for (const [control, key] of Object.entries(COVERED))
        questions[key] = withControlEffects(questions[key], control, COVERED_SCORES[key]);
    return questions;
}
/**
 * The insurer questions (tasks 88–89): which forms to prepare, and the underwriting facts. A fact is shown
 * only when a chosen form needs it, and must then be answered unless it is optional on every form.
 */
function insurerQuestions() {
    const forms = Object.fromEntries(Object.entries(INSURER_FORMS).map(([k, f]) => [k, InsurerFormDefinition.parse(f)]));
    // Shown when a chosen form reads it: the bundle adds that to its own visibility (compileForms, task 96).
    const needs = formQuestionNeeds(forms);
    const facts = Object.entries(UNDERWRITING_FACTS).map(([key, q]) => {
        if (!needs.has(key))
            throw new Error(`${key} isn’t read by any insurer form`);
        return [key, { ...q, mandatory: q.mandatory ?? true, for_forms: true }];
    });
    return {
        [FORMS_QUESTION]: {
            type: 'multiple_choice',
            text: 'Which insurers’ proposal forms should this assessment prepare?',
            help: 'Your broker usually chooses. Each form adds the questions that insurer asks; one answer fills every form that asks it.', // prettier-ignore
            options: Object.entries(forms).map(([value, f]) => ({ value, label: `${f.insurer}: ${f.product}` })),
        },
        ...Object.fromEntries(facts),
    };
}
/**
 * A question for every control in the library, whatever the industry, plus the hand-written and profile
 * questions. Industry-pack questions are shown only to their industry, so this set can score any business;
 * the scoring reference fixtures use it for the industries that have no journey yet.
 */
export function libraryQuestions() {
    const questions = baseQuestions();
    for (const [id, c] of Object.entries(CONTROL_LIBRARY))
        if (!COVERED[id])
            questions[id] = controlQuestion(id, c);
    // As a bundle carries them: the insurer questions shown when a chosen form reads them (task 96).
    return compileForms({ question: questions, insurer_form: { ...INSURER_FORMS } }).question; // prettier-ignore
}
export function manufacturingJourneyMY() {
    const controls = Object.entries(CONTROL_LIBRARY);
    const questions = baseQuestions();
    const generated = {
        S01: [], S02: [], S03: [], S04: [], S05: [], S06: [], S07: [], S08: [], S09: [], S10: [], S11: [], S12: [],
    }; // prettier-ignore
    for (const [id, c] of controls.filter(([id]) => !COVERED[id])) {
        questions[id] = controlQuestion(id, c);
        // Manufacturing's pack sits with OT security; the other industries' packs in "Your industry".
        const industries = c.applies?.industries;
        const stage = industries
            ? industries.includes('manufacturing')
                ? 'S06'
                : 'S09'
            : (STAGE_OF_CONTROL[id] ?? STAGE_OF_DOMAIN[c.domain]);
        generated[stage].push([id, c]);
    }
    // Within a stage: kill switches, then mandatory, then by criticality.
    const rank = ([id, c]) => [c.kill_switch ? 0 : 1, questions[id].mandatory ? 0 : 1, -c.criticality];
    const order = (a, b) => {
        const [x, y] = [rank(a), rank(b)];
        return x[0] - y[0] || x[1] - y[1] || x[2] - y[2] || a[0].localeCompare(b[0]);
    };
    const stages = Object.fromEntries(Object.keys(STAGES).map((key) => {
        const asked = [...FIRST[key], ...generated[key].sort(order).map(([id]) => id)];
        // A kill switch's probes come straight after the question that asks about it (task 86).
        // That includes the pack controls an industry profile makes a kill switch (task 79).
        const probesAfter = new Map(Object.keys(CONTROL_LIBRARY)
            .filter((id) => CONTROL_LIBRARY[id].kill_switch || PROFILE_KILL_SWITCHES.includes(id))
            .map((id) => [PROBE_PARENT[id] ?? id, probeKeys(id)]));
        const keys = asked.flatMap((k) => [k, ...(probesAfter.get(k) ?? [])]);
        const mandatory = asked.filter((k) => questions[k].mandatory).length;
        const minutes = Math.max(1, Math.round(mandatory * 0.5 + (asked.length - mandatory) * 0.25));
        // After the profile, the most valuable question comes next (decision 0022, task 86).
        // The insurer stages follow the forms' order: their questions earn no score to rank by.
        const ranked = !['S01', 'S08', 'S10', 'S11', 'S12'].includes(key) ? { order: 'ranked' } : {};
        return [key, { ...STAGES[key], ...ranked, questions: keys, estimated_minutes: minutes }];
    }));
    const journey = {
        name: 'SME Cyber Assessment (MY)',
        description: 'The full assessment for Malaysian SMEs: the common controls, plus the pack for the business’s industry.',
        stages: [
            { stage: 'S01' },
            { stage: 'S02', after: ['S01'] },
            { stage: 'S03', after: ['S01'] },
            { stage: 'S04', after: ['S02', 'S03'] },
            { stage: 'S05', after: [{ stage: 'S04', when: 'started' }] },
            { stage: 'S06', after: ['S05'] },
            { stage: 'S09', after: ['S05'] },
            { stage: 'S07', after: ['S05'] },
            { stage: 'S08', after: ['S04', 'S05', 'S07'] },
            { stage: 'S10', after: ['S08'] },
            { stage: 'S11', after: ['S08'] },
            { stage: 'S12', after: ['S08'] },
        ],
    };
    return [
        ...controls.map(([key, body]) => ({ kind: 'control', key, body })),
        ...Object.entries(questions).map(([key, body]) => ({ kind: 'question', key, body })),
        ...Object.entries(stages).map(([key, body]) => ({ kind: 'stage', key, body })),
        { kind: 'journey', key: MFG_MY_JOURNEY, body: journey },
        ...Object.entries(STARTER_RULES).map(([key, body]) => ({ kind: 'rule', key, body })),
        ...Object.entries(DOCUMENT_TYPES).map(([key, body]) => ({ kind: 'document_type', key, body })),
        ...Object.entries(industryProfiles(MFG_MY_JOURNEY)).map(([key, body]) => ({ kind: 'industry_profile', key, body })), // prettier-ignore
        ...Object.entries(INSURER_FORMS).map(([key, body]) => ({ kind: 'insurer_form', key, body })),
    ];
}
//# sourceMappingURL=manufacturing-my.js.map